import { useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { AppEstimator } from "../estimator/AppEstimator";

// Wizard (spec §9). Connect + Build are wired to the real Convex backend
// (Slack/Discord OAuth, workspace scan, live quote, dynamic Stripe checkout).
const STEPS = [
  { key: "connect", label: "Connect", hint: "Slack + Discord" },
  { key: "build", label: "Build migration", hint: "Scan · price · pay" },
  { key: "migrate", label: "Watch it live", hint: "Live dashboard" },
] as const;

export function OnboardingWizard({
  tenantId,
  tenantName,
}: {
  tenantId: Id<"tenants">;
  tenantName: string;
}) {
  const [active, setActive] = useState(0);

  return (
    <div className="wizard">
      <div className="wizard-steps">
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            className="wstep"
            data-state={i < active ? "done" : i === active ? "active" : "todo"}
            onClick={() => setActive(i)}
          >
            <span className="wstep-n">{i < active ? "✓" : i + 1}</span>
            <span>
              <b>{s.label}</b>
              <span>{s.hint}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="card panel">
        {active === 0 && (
          <ConnectStep tenantId={tenantId} tenantName={tenantName} onNext={() => setActive(1)} />
        )}
        {active === 1 && <BuildMigrationStep tenantId={tenantId} />}
        {active === 2 && (
          <ComingSoon
            title="Watch it live"
            body="Once a migration is running, the live per-channel scoreboard streams here in real time — the same view your team sees inside Discord. Open the Live dashboard tab to follow along."
          />
        )}
      </div>
    </div>
  );
}

function ConnectStep({
  tenantId,
  tenantName,
  onNext,
}: {
  tenantId: Id<"tenants">;
  tenantName: string;
  onNext: () => void;
}) {
  const status = useQuery(api.credentials.getConnectionStatus, { tenantId });
  const startSlack = useAction(api.credentials.startSlackConnect);
  const startDiscord = useAction(api.credentials.startDiscordConnect);
  const [busy, setBusy] = useState<null | "slack" | "discord">(null);
  const [error, setError] = useState<string | null>(null);

  async function connect(which: "slack" | "discord") {
    setBusy(which);
    setError(null);
    try {
      const { url } =
        which === "slack"
          ? await startSlack({ tenantId })
          : await startDiscord({ tenantId });
      // Hand off to the provider's consent screen; we return to the app after.
      window.location.href = url;
    } catch {
      setError(
        which === "slack"
          ? "Slack connect isn’t available yet — the app credentials are still being provisioned."
          : "Discord connect isn’t available yet — the app credentials are still being provisioned.",
      );
      setBusy(null);
    }
  }

  const slack = status?.slack;
  const discord = status?.discord;
  const bothConnected = slack?.connected && discord?.connected;

  return (
    <>
      <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", marginBottom: 8 }}>
        Connect {tenantName}
      </h3>
      <p className="muted" style={{ marginBottom: 20, maxWidth: "58ch" }}>
        Invite our bot into Slack and Discord — no bot-building, no token pasting.
        We store only what’s needed, encrypted, and never expose tokens to your
        browser.
      </p>

      {error && <p className="form-error" style={{ marginBottom: 16 }}>{error}</p>}

      <div className="panel-connect">
        <ConnectCard
          letter="S"
          gradient="linear-gradient(160deg, var(--slack), var(--slack-core))"
          title="Add to Slack"
          blurb="Grants read access to your channels and history for migration."
          connected={slack?.connected ?? false}
          connectedLabel={slack?.connected ? slack.teamName ?? "Workspace connected" : null}
          busy={busy === "slack"}
          loading={status === undefined}
          onClick={() => connect("slack")}
        />
        <ConnectCard
          letter="D"
          gradient="linear-gradient(160deg, var(--discord), var(--discord-core))"
          title="Add to Discord"
          blurb="Invites our bot (full control) so it can create channels and post your history."
          connected={discord?.connected ?? false}
          connectedLabel={
            discord?.connected ? discord.guildName ?? "Server connected" : null
          }
          busy={busy === "discord"}
          loading={status === undefined}
          onClick={() => connect("discord")}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <button className="btn btn-primary btn-sm" disabled={!bothConnected} onClick={onNext}>
          {bothConnected ? "Next: select channels" : "Connect both to continue"}
        </button>
      </div>
    </>
  );
}

function ConnectCard({
  letter,
  gradient,
  title,
  blurb,
  connected,
  connectedLabel,
  busy,
  loading,
  onClick,
}: {
  letter: string;
  gradient: string;
  title: string;
  blurb: string;
  connected: boolean;
  connectedLabel: string | null;
  busy: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <div className="connect-card">
      <span className="pole-badge" style={{ background: gradient }}>
        {letter}
      </span>
      <b style={{ fontFamily: "var(--font-display)" }}>{title}</b>
      <span className="muted" style={{ fontSize: "0.88rem" }}>
        {blurb}
      </span>
      {connected ? (
        <span className="pill" style={{ alignSelf: "flex-start" }}>
          ✓ {connectedLabel}
        </span>
      ) : (
        <button
          className="btn btn-ghost btn-sm"
          onClick={onClick}
          disabled={busy || loading}
        >
          {busy ? "Redirecting…" : loading ? "Checking…" : title}
        </button>
      )}
    </div>
  );
}

function BuildMigrationStep({ tenantId }: { tenantId: Id<"tenants"> }) {
  const status = useQuery(api.credentials.getConnectionStatus, { tenantId });
  const scan = useQuery(api.scan.getScan, { tenantId });
  const runScan = useAction(api.scan.scanWorkspace);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captureEmails, setCaptureEmails] = useState(false);

  const slackConnected = status?.slack.connected ?? false;

  async function doScan() {
    setScanning(true);
    setError(null);
    try {
      await runScan({ tenantId, captureEmails });
    } catch {
      setError("Couldn’t scan your workspace. Please try again.");
    } finally {
      setScanning(false);
    }
  }

  if (status === undefined) {
    return <p className="muted mono" style={{ fontSize: "0.86rem" }}>Loading…</p>;
  }

  if (!slackConnected) {
    return (
      <ComingSoon
        title="Connect Slack first"
        body="Add our bot to your Slack workspace on the Connect step. We’ll scan your real channels — message counts and media sizes — so you can price and pick exactly what migrates."
      />
    );
  }

  // Never scanned yet (and not currently scanning) → invite the scan.
  if (scan === null && !scanning) {
    return (
      <div className="scan-cta">
        <h3 className="build-title">Scan your workspace</h3>
        <p className="muted" style={{ maxWidth: "56ch", margin: "8px auto 20px" }}>
          We’ll read your channels, message counts, and media sizes (read-only) so
          your quote is exact — down to the channel. Nothing moves yet.
        </p>
        <EmailCaptureToggle checked={captureEmails} onChange={setCaptureEmails} />
        {error && <p className="form-error" style={{ margin: "12px 0" }}>{error}</p>}
        <button className="btn btn-primary" onClick={doScan}>
          Scan workspace
        </button>
      </div>
    );
  }

  if (scanning || scan === undefined) {
    return (
      <div className="scan-cta">
        <div className="spinner" style={{ margin: "0 auto 18px" }} />
        <h3 className="build-title">Scanning your workspace…</h3>
        <p className="muted mono" style={{ fontSize: "0.86rem" }}>
          Reading channels &amp; history — this can take a minute for years of data.
        </p>
      </div>
    );
  }

  if (!scan) return null; // unreachable: handled above, narrows the type

  const scannedAgo = new Date(scan.scannedAt).toLocaleString();
  return (
    <>
      <div className="build-head">
        <div>
          <h3 className="build-title">Build your migration</h3>
          <p className="muted mono build-scanmeta">
            {scan.channels.length} channels scanned · {scannedAgo}
            {typeof scan.emailsCaptured === "number" &&
              ` · ${scan.emailsCaptured} team emails captured`}
          </p>
        </div>
        <div className="stack" style={{ gap: 8, alignItems: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={doScan} disabled={scanning}>
            Rescan
          </button>
          <label className="email-inline mono">
            <input
              type="checkbox"
              checked={captureEmails}
              onChange={(e) => setCaptureEmails(e.target.checked)}
            />
            capture team emails on rescan
          </label>
        </div>
      </div>
      {error && <p className="form-error" style={{ marginBottom: 12 }}>{error}</p>}
      <AppEstimator
        tenantId={tenantId}
        channels={scan.channels}
        truncated={scan.truncated}
        premiumTier={scan.discordPremiumTier ?? null}
      />
    </>
  );
}

function EmailCaptureToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="email-capture">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <b>Capture team emails</b> <span className="soon-tag">Optional</span>
        <span className="email-capture-help">
          If enabled, we capture your active members’ email addresses so that
          later — when our managed onboarding launches — we can invite your whole
          team to your new Discord and set them up automatically. Capturing emails
          is what lets you bring <em>everyone</em> over and fully leave Slack. We
          never share your emails or use them for anything else, and they’re
          stored encrypted. You can leave this off.
        </span>
      </span>
    </label>
  );
}

function ComingSoon({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty" style={{ border: "none", padding: "20px 0" }}>
      <div className="empty-ico" aria-hidden="true">
        ⏳
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>{title}</h3>
        <span className="soon-tag">Coming soon</span>
      </div>
      <p className="muted" style={{ maxWidth: "52ch" }}>
        {body}
      </p>
    </div>
  );
}
